import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import { FwDialogService } from '@flywheel-io/vision';
import get from 'lodash/get';
import { firstValueFrom, Subject } from 'rxjs';

import { ConfigService } from './config.service';
import { SessionTimeoutErrorComponent } from './fatal-errors/session-timeout-error.component';
import { LoggingService } from './logging.service';

export const AUTH_LOCAL_STORAGE_TOKEN = 'satellizer_token';
const defaultSessionTimeout = 12 * 60 * 60; // 12 hours

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public tokenRefreshed$ = new Subject<void>(); // emits when the auth0 token is refreshed
  private auth0iat: number
  private authRefreshHandle: null | ReturnType<typeof setTimeout> = null
  private initialAuthComplete = false // used to prevent multiple calls to /api/login/auth0
  private resetSessionTimeoutChannel = new BroadcastChannel('reset_session_timeout')
  private sessionTimeoutRef: ReturnType<typeof setTimeout>
  private unguardedHttp: HttpClient // makes requests without attaching auth

  constructor(
    httpBackend: HttpBackend,
    private config: ConfigService,
    private dialogService: FwDialogService,
    private guardedHttp: HttpClient,
    private router: Router,
    private auth0: Auth0Service,
    private logger: LoggingService,
  ) {
    this.unguardedHttp = new HttpClient(httpBackend);
    auth0.idTokenClaims$.subscribe((idToken => this.auth0iat = idToken?.iat));

    this.resetSessionTimeout();
    this.resetSessionTimeoutChannel.addEventListener('message', () => {
      // make sure we don't rebroadcast to the sender
      this.resetSessionTimeout({ broadcast: false });
    });
  }

  get isLoggedIn(): boolean {
    return this.getBearerToken()?.length > 0;
  }

  private get inactivityTimeout(): number {
    // timeout in seconds
    const siteTimeout = parseInt(String(get(this.config, 'apiConfig.site.inactivity_timeout'))) || defaultSessionTimeout;
    const overrideTimeout = parseInt(String(this.config.getFeatureSetting('override_inactivity_timeout')));
    if (overrideTimeout > 0 && overrideTimeout < siteTimeout) {
      return overrideTimeout;
    }
    return siteTimeout;
  }

  /**
   * Process the auth0 token from one localStorage item to a satellized equivelent
   * This is required for exchange/ohif viewers/service workers to authenticate!
   */
  storeAuth0Token(): void {
    const storage = { ...localStorage };
    const storageKeys = Object.keys(storage);
    // escape (prepend a backslash to) any special (regex) characters in the audience
    const escapedAudience = this.config.authAudience.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const tokenRegex = new RegExp(`@@auth0spajs@@.*::${escapedAudience}`);
    // Find all matching tokens and select the one with the greatest expiresAt
    let maxExpiresAt = -1;
    let newestAuthItem = null;
    for (const key of storageKeys) {
      if (tokenRegex.test(key)) {
        try {
          const authItem = JSON.parse(storage[key]) || {};
          if (authItem?.expiresAt && authItem.expiresAt > maxExpiresAt) {
            maxExpiresAt = authItem.expiresAt;
            newestAuthItem = authItem;
          }
          else if (authItem?.body?.access_token && !authItem.expiresAt && newestAuthItem === null) {
            newestAuthItem = authItem; // fallback to first item if no expiresAt found
          }
        } catch (e) {
          // ignore parse errors
        }
      }
    }
    if (newestAuthItem) {
      const newToken = newestAuthItem?.body?.access_token;
      if (!newToken) {
        return;
      }
      this.setToken(`Bearer ${newToken}`);
      // refreshing 60s before exp
      const now = Date.now();
      const refreshDelay = newestAuthItem?.expiresAt ? ((newestAuthItem.expiresAt * 1000) - now - 60000) : 0;
      if (refreshDelay > 0) {
        this.scheduleTokenRefresh(refreshDelay);
      }
    }
  }

  /**
   * Store a non-auth0 token for auth
   */
  async setToken(newToken: string): Promise<void> {
    const oldToken = this.getBearerToken();
    const isSameToken = oldToken === newToken;

    if (!this.initialAuthComplete && this.config.isAuth0Enabled()) {
      this.logAuth0Login();
      this.initialAuthComplete = true;
    }

    if (isSameToken) {
      return;
    }

    this.storeSatellizedToken(newToken);

    this.tokenRefreshed$.next();

    if (oldToken) {
      this.invalidateToken(oldToken, true);
    }
  }

  /**
   * Parse the satellized token and return the id_token in a Bearer format
   */
  getBearerToken(): string | null {
    // auth0 tokens are now being stashed in the satellizer token as well
    const item = localStorage.getItem(AUTH_LOCAL_STORAGE_TOKEN);
    return this.decodeSatellizedToken(item);
  }

  getBasicToken(): string | null {
    let token = this.getBearerToken();
    if (token) {
      token = token.replace(/^Bearer /, ''); // remove the 'Bearer ' prefix for basic auth
    }
    return token;
  }

  async logout(): Promise<void> {
    await this.invalidateToken(this.getBearerToken());
    localStorage.removeItem(AUTH_LOCAL_STORAGE_TOKEN);
    if (this.config.isAuth0Enabled()) {
      this.auth0.logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });
      return;
    }
    this.router.navigate(['/']).then(() => window.location.reload());
  }

  resetSessionTimeout({ broadcast = true } = {}): void {
    clearTimeout(this.sessionTimeoutRef);
    const inactivityTimeoutMs = this.inactivityTimeout * 1000;
    if (inactivityTimeoutMs > 0) {
      this.sessionTimeoutRef = setTimeout((() => {
        this.sessionInactive();
      }), inactivityTimeoutMs);
      if (broadcast) {
        this.resetSessionTimeoutChannel.postMessage(inactivityTimeoutMs);
      }
    }
  }

  private storeSatellizedToken(token: string): void {
    localStorage.setItem(AUTH_LOCAL_STORAGE_TOKEN, this.satellizeToken(token));
  }

  private async logAuth0Login(): Promise<void> {
    try {
      await firstValueFrom(this.guardedHttp.get<null>(this.config.getApiUrl('/api/login/auth0')));
    } catch (error) {
      this.logger.warn('Failed to log Auth0 login:', error);
    }
  }

  private scheduleTokenRefresh(refreshInterval: number): void {
    clearTimeout(this.authRefreshHandle);
    this.authRefreshHandle = setTimeout(async () => {
      const now = Math.floor(Date.now() / 1000);
      const timeoutExceeded = this.auth0iat && now > this.auth0iat + this.inactivityTimeout;

      if (timeoutExceeded) {
        return this.sessionInactive();
      }

      try {
        await firstValueFrom(this.auth0.getAccessTokenSilently({ cacheMode: 'off' }));
        this.storeAuth0Token();
      } catch (error) {
        this.logger.warn(error);
      }
    }, refreshInterval);
  }

  /**
   *
   * @param token - expects a string starting with `Bearer `
   * @returns encoded string for localstorage
   */
  private satellizeToken(token: string): string {
    const reversedToken = this.reverseString(btoa(this.reverseString(token)));
    const padding = (4 - (reversedToken.length % 4)) % 4;
    const paddedToken = reversedToken + '='.repeat(padding);
    return `v1:${paddedToken}:${padding}`;
  }

  /**
   *
   * @param token - expects a string matching the output format of satellizeToken
   * @returns decoded string starting with `Bearer `
   */
  private decodeSatellizedToken(token: string): string {
    if (!token || !token.startsWith('v1:')) {
      return token || '';
    }
    let [, value, padding] = token.split(':'); // eslint-disable-line
    for (let i = 0; i < parseInt(padding, 10); i++) {
      value = '=' + value;
    }
    const fullToken = this.reverseString(atob(this.reverseString(value)));
    const parts = fullToken.split(' ');
    const tokenStub = parts[1] || parts[0];
    return `Bearer ${tokenStub}`;
  }

  private reverseString(str: string): string {
    return Array.from(str).reverse().join('');
  }

  private async invalidateToken(token?: string, refreshOnly = false): Promise<void> {
    if (token) {
      await firstValueFrom(this.unguardedHttp.post<null>(this.config.getApiUrl(`/api/logout?refresh_only=${refreshOnly}`), null, {
        headers: { Authorization: token },
      })).catch(error => {
        this.logger.warn(error);
      });
    }
  }

  private sessionInactive(): void {
    this.dialogService.open(SessionTimeoutErrorComponent, {
      height: '100vh',
      width: '100vw',
    });
  }
}
